import { ActionIcon, Box, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconFocus2 } from '@tabler/icons-react';
import { featureCollection, point } from '@turf/turf';
import { MapEvent, MapMouseEvent } from 'mapbox-gl';
import { FC, useCallback, useMemo, useRef } from 'react';
import { MapRef, NavigationControl } from 'react-map-gl';

import mobileClasses from '../../styles/farmer-mobile.module.css';
import mapClasses from '../../styles/harvest-proof-map.module.css';
import {
  buildFieldsFeatureCollection,
  findInsidePendingAnswer,
  rankAnswersByDistance,
} from '../../utils/harvest-proof-fields';
import { FieldsList } from '../fields-list/fields-list';
import { UserPositionMarker } from '../user-position-marker/user-position-marker';

import { HarvestProofAnswerPublic, isHarvestProofAnswerSubmitted } from '@harvest-proof/data-access';
import { PfMap, PlotsLayer, PLOTS_LAYER_ID, zoomOnFeature } from '@map';


interface FieldsOverviewProps {
  answers: HarvestProofAnswerPublic[];
  position: GeolocationPosition | null;
  onOpenCapture: (answer: HarvestProofAnswerPublic) => void;
}

export const FieldsOverview: FC<FieldsOverviewProps> = ({
  answers,
  position,
  onOpenCapture,
}) => {
  const mapRef = useRef<MapRef>(null);
  const latitude = position?.coords.latitude;
  const longitude = position?.coords.longitude;

  const rankedAnswers = useMemo(
    () => rankAnswersByDistance(answers, latitude, longitude),
    [answers, latitude, longitude],
  );

  const insideAnswer = useMemo(
    () => findInsidePendingAnswer(rankedAnswers),
    [rankedAnswers],
  );

  const fieldsData = useMemo(
    () =>
      buildFieldsFeatureCollection(answers, {
        insideAnswerId: insideAnswer?.id,
      }),
    [answers, insideAnswer?.id],
  );

  const fitAllFields = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }

    const fieldFeatures = answers
      .map((answer) => answer.field)
      .filter((field) => Boolean(field?.geometry));

    const allFeatures: GeoJSON.Feature[] = [
      ...(fieldFeatures as GeoJSON.Feature[]),
    ];

    if (
      latitude != null &&
      longitude != null &&
      Number.isFinite(latitude) &&
      Number.isFinite(longitude)
    ) {
      allFeatures.push(point([longitude, latitude]));
    }

    if (allFeatures.length === 0) {
      return;
    }

    zoomOnFeature({
      map,
      geojson: featureCollection(allFeatures),
      padding: { top: 40, bottom: 40, left: 24, right: 24 },
    });
  }, [answers, latitude, longitude]);

  const handleLoad = useCallback(
    (event: MapEvent) => {
      const map = event.target;
      const fieldFeatures = answers
        .map((answer) => answer.field)
        .filter((field) => Boolean(field?.geometry));

      if (fieldFeatures.length === 0) {
        return;
      }

      const allFeatures: GeoJSON.Feature[] = [
        ...(fieldFeatures as GeoJSON.Feature[]),
      ];

      if (
        latitude != null &&
        longitude != null &&
        Number.isFinite(latitude) &&
        Number.isFinite(longitude)
      ) {
        allFeatures.push(point([longitude, latitude]));
      }

      zoomOnFeature({
        map,
        geojson: featureCollection(allFeatures),
        padding: { top: 40, bottom: 40, left: 24, right: 24 },
      });
    },
    [answers, latitude, longitude],
  );

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      const features = event.target.queryRenderedFeatures(event.point, {
        layers: [PLOTS_LAYER_ID, `${PLOTS_LAYER_ID}_outline`],
      });

      const answerId = features[0]?.properties?.['answerId'];
      if (answerId == null) {
        return;
      }

      const answer = answers.find((item) => item.id === Number(answerId));
      if (!answer || isHarvestProofAnswerSubmitted(answer)) {
        return;
      }

      onOpenCapture(answer);
    },
    [answers, onOpenCapture],
  );

  const handleSelectFromList = useCallback(
    (answer: HarvestProofAnswerPublic) => {
      if (isHarvestProofAnswerSubmitted(answer)) {
        return;
      }
      onOpenCapture(answer);
    },
    [onOpenCapture],
  );

  return (
    <Stack gap={0}>
      {insideAnswer ? (
        <Box className={mobileClasses.banner}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm">
              Vous êtes sur{' '}
              <Text component="span" fw={700}>
                {insideAnswer.field.properties?.name}
              </Text>
            </Text>
            <Button
              className={mobileClasses.touchButton}
              onClick={() => onOpenCapture(insideAnswer)}
              size="md"
            >
              Continuer
            </Button>
          </Group>
        </Box>
      ) : null}

      <Box className={mobileClasses.divider}>
        <Group className={mobileClasses.sectionPad} justify="space-between">
          <Text fw={600}>Carte des parcelles</Text>
          <Tooltip label="Recentrer sur toutes les parcelles">
            <ActionIcon
              aria-label="Recentrer sur toutes les parcelles"
              onClick={fitAllFields}
              size="xl"
              variant="light"
            >
              <IconFocus2 size={22} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Box className={mapClasses.mapWrapperOverview}>
          <PfMap
            classnames={{ container: mapClasses.mapInner }}
            cursor="pointer"
            onClick={handleMapClick}
            onLoad={handleLoad}
            ref={mapRef}
          >
            <NavigationControl position="top-right" showCompass={false} />
            <PlotsLayer data={fieldsData} />
            {latitude != null && longitude != null ? (
              <UserPositionMarker latitude={latitude} longitude={longitude} />
            ) : null}
          </PfMap>
        </Box>
        <Text c="dimmed" className={mobileClasses.sectionPad} pb="sm" size="sm">
          Touchez une parcelle sur la carte ou dans la liste.
        </Text>
      </Box>

      <FieldsList items={rankedAnswers} onSelect={handleSelectFromList} />
    </Stack>
  );
};
